import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import GenbankSample from '../genbank/genbank-sample';
import {getQASummary} from './utils';


export default class SeqLoadSummary extends React.Component {

  static propTypes = {
    rawDataCollection: PropTypes.object.isRequired,
  };

  constructor() {
    super(...arguments);
    this.state = {
      loadFromInfo: '',
      sequenceGeneInfo: '',
      sequenceCloneInfo: '',
      sequenceQAInfo: '',
      speciesInfo: '',
      regionInfo: '',
      sampleSourceInfo: '',
      isolateDateInfo: '',
    };
  }

  componentDidMount() {
    this.updateLoadFromInfo();
    this.updateSequenceGeneInfo();
    this.updateSequenceCloneInfo();
    this.updateSequenceQAInfo();
    this.updateSpeciesInfo();
    this.updateRegionInfo();
    this.updateSampleSourceInfo();
    this.updateIsolateDateInfo();
  }

  get shouldShow() {
    const {rawDataCollection} = this.props;
    const {references, rawSequences, rawAlignments} = rawDataCollection;

    if (!references) {
      return false;
    }
    if (references.length === 0) {
      return false;
    }
    if (!rawSequences) {
      return false;
    }
    if (rawSequences.length === 0) {
      return false;
    }
    if (!rawAlignments) {
      return false;
    }
    if (rawAlignments.length === 0) {
      return false;
    }
    return true;
  }

  updateLoadFromInfo() {
    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    let loadFromInfo = (
      <span>
        {
          `${rawSequences.length} ` +
          `${rawSequences.length > 1? 'sequences' : 'sequence'}` +
          ' ' +
          `from ${rawDataCollection.loadFrom}:` +
          ' '
        }
      </span>
    );
    this.setState({loadFromInfo});
  }

  updateSequenceGeneInfo() {

    const {rawDataCollection} = this.props;
    const {rawAlignments} = rawDataCollection;

    const summary = rawAlignments.reduce((accu, aligned) => {
      for (const gene of aligned.geneSequences) {
        const geneName = gene.gene;
        accu[geneName] = (geneName in accu) ? accu[geneName] : 0;
        accu[geneName] += 1;
      }
      return accu;
    }, {});

    const spliter = ', ';
    const summary_length = Object.entries(summary).length;

    let sequenceGeneInfo = (
      <span>
        {Object.entries(summary).map(([geneName, number], idx) => {
          const showSpliter = (idx < (summary_length - 1));
          return (
            <span key={idx}>
              {geneName}{' '}({number})
              {showSpliter? spliter: null}
            </span>
          );
        })}
      </span>
    );

    this.setState({sequenceGeneInfo});

  }

  updateSequenceCloneInfo() {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    const cloneSeq = rawSequences.filter((seq) => {
      return seq.cloneName && seq.cloneName !== '';
    });

    const cloneSeqLength = cloneSeq.length;

    if (cloneSeqLength === 0) {
      return null;
    }

    let sequenceCloneInfo = (
      <p>
        Cloned: {cloneSeqLength}
      </p>
    );

    this.setState({sequenceCloneInfo});
    return sequenceCloneInfo;
  }

  updateSequenceQAInfo() {

    const {rawDataCollection} = this.props;
    rawDataCollection.validate();

    let sequenceQAInfo = getQASummary(rawDataCollection);
    this.setState({sequenceQAInfo});
  }

  updateSpeciesInfo() {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    const summary = rawSequences.reduce((accu, seq) => {
      const species = seq.species || 'NA';
      accu[species] = accu[species] || 0;
      accu[species] += 1;
      return accu;
    }, {});

    const spliter = ', ';
    const summary_length = Object.entries(summary).length;
    let speciesInfo = (
      <span>
        {'Species: '}
        {Object.entries(summary).map(([species, number], idx) => {
          const showSpliter = (idx < (summary_length - 1));
          return (
            <span key={idx}>
              {species}{' '}({number})
              {showSpliter? spliter: null}
            </span>
          );
        })}
      </span>
    );

    this.setState({speciesInfo});
  }

  updateRegionInfo() {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    const summary = rawSequences.reduce((accu, seq) => {
      const region = seq.region || 'NA';
      accu[region] = accu[region] || 0;
      accu[region] += 1;
      return accu;
    }, {});

    const spliter = ', ';
    const summary_length = Object.entries(summary).length;
    let regionInfo = (
      <span>
        {'Region: '}
        {Object.entries(summary).map(([region, number], idx) => {
          const showSpliter = (idx < (summary_length - 1));
          return (
            <span key={idx}>
              {region}{' '}({number})
              {showSpliter? spliter: null}
            </span>
          );
        })}
      </span>
    );

    this.setState({regionInfo});
  }

  updateSampleSourceInfo() {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    const summary = rawSequences.reduce((accu, seq) => {
      const source = seq.source || 'NA';
      accu[source] = accu[source] || 0;
      accu[source] += 1;
      return accu;
    }, {});

    const spliter = ', ';
    const summary_length = Object.entries(summary).length;
    let sampleSourceInfo = (
      <span>
        {'Source: '}
        {Object.entries(summary).map(([source, number], idx) => {
          const showSpliter = (idx < (summary_length - 1));
          return (
            <span key={idx}>
              {source}{' '}({number})
              {showSpliter? spliter: null}
            </span>
          );
        })}
      </span>
    );

    this.setState({sampleSourceInfo});
  }

  updateIsolateDateInfo() {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    let isolateDateMin = null;
    let isolateDateMax = null;
    rawSequences.map((seq) => {
      if (!seq.isolateDate) {
        return;
      }
      const isolateDate =  moment.utc(seq.isolateDate);

      if (!isolateDateMax) {
        isolateDateMax = isolateDate;
      } else if (isolateDate.isAfter(isolateDateMax)) {
        isolateDateMax = isolateDate;
      }

      if (!isolateDateMin) {
        isolateDateMin = isolateDate;
      } else if (isolateDate.isBefore(isolateDateMin)) {
        isolateDateMin = isolateDate;
      }
    });

    let isolateDateInfo = '';

    if (!isolateDateMin && !isolateDateMax) {
      isolateDateInfo = (
        <span>
          Isolate date: NA
        </span>
      );
    } else if (isolateDateMin === isolateDateMax) {
      isolateDateInfo = (
        <span>
          {
           'Isolate date: ' +
           `${isolateDateMin.format('YYYY')}`
          }
        </span>
      );
    } else {
      isolateDateInfo = (
        <span>
          {
           'Isolate date from' +
           ` ${isolateDateMin.format('YYYY')} ` +
           'to' +
           ` ${isolateDateMax.format('YYYY')} `
          }
        </span>
      );
    }

    this.setState({isolateDateInfo});
  }

  render() {
    const {shouldShow} = this;
    if (!shouldShow) {
      return null;
    }

    const {rawDataCollection} = this.props;

    return (
      <div>
        <h3>
          Sequence loading summary:
        </h3>
        <p>
          {this.state.loadFromInfo}
          {this.state.sequenceGeneInfo}
        </p>
        {this.state.sequenceCloneInfo}
        <p>
          {this.state.sequenceQAInfo}
        </p>
        <p>
          {this.state.speciesInfo}
          {<span> / </span>}
          {this.state.regionInfo}
          {<span> / </span>}
          {this.state.isolateDateInfo}
          {<span> / </span>}
          {this.state.sampleSourceInfo}
        </p>
        <GenbankSample
         rawDataCollection={rawDataCollection}
        />
        <hr/>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/wizard/isolate/upload/summary.js